/**
 * AddToListDialog - Dialog for adding media items to watchlists
 * Supports adding to top, bottom, or ranked position
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { List, Check, ArrowUp, ArrowDown, ListOrdered } from 'lucide-react';

export function AddToListDialog({
  open,
  onClose,
  item,
  watchlists = [],
  onAddToList,
  onAddAndRank,
  onCreateList,
  loading = false,
  mediaType = 'tv', // 'tv' | 'movie' | 'podcast'
  testId = 'add-to-list-dialog'
}) {
  const [selectedListId, setSelectedListId] = useState(null);
  
  const handleClose = () => {
    setSelectedListId(null);
    onClose();
  };
  
  const handleAdd = (position) => {
    if (selectedListId && item) {
      onAddToList(selectedListId, item, position);
    }
  };
  
  const handleAddAndRank = () => {
    const selectedList = watchlists.find(l => l.id === selectedListId);
    if (selectedList && item) {
      onAddAndRank(selectedList, item);
    }
  };
  
  const getMediaTypeLabel = () => {
    switch (mediaType) {
      case 'movie': return 'films';
      case 'podcast': return 'podcasts';
      case 'book': return 'books';
      default: return 'TV shows';
    }
  };
  
  const selectedList = watchlists.find(l => l.id === selectedListId);
  const itemCount = mediaType === 'podcast' 
    ? selectedList?.podcasts?.length || 0
    : mediaType === 'book'
    ? selectedList?.books?.length || selectedList?.series?.length || 0
    : selectedList?.series?.length || 0;
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-outfit text-[#3D4F6F]">Add to List</DialogTitle>
          <DialogDescription className="text-[#64748B]">
            Choose a list for &ldquo;{item?.title || item?.name}&rdquo;
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {watchlists.length === 0 ? (
            <div className="text-center py-6">
              <List className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-[#64748B] mb-4">No {getMediaTypeLabel()} lists yet</p>
              <Button
                onClick={onCreateList}
                className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
              >
                Create a List
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-2 max-h-[200px] overflow-y-auto mb-4">
                {watchlists.map((list) => {
                  const count = mediaType === 'podcast' 
                    ? list.podcasts?.length || 0 
                    : mediaType === 'book'
                    ? list.books?.length || list.series?.length || 0
                    : list.series?.length || 0;
                  
                  return (
                    <button
                      key={list.id}
                      onClick={() => setSelectedListId(list.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                        selectedListId === list.id 
                          ? 'border-[#F87171] bg-[#F87171]/5' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`select-list-${list.id}`}
                    >
                      <List className={`w-5 h-5 ${selectedListId === list.id ? 'text-[#F87171]' : 'text-slate-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#3D4F6F] truncate">{list.title}</p>
                        <p className="text-xs text-[#64748B]">{count} items</p>
                      </div>
                      {selectedListId === list.id && <Check className="w-5 h-5 text-[#F87171]" />}
                    </button>
                  );
                })}
              </div>
              
              {selectedListId && (
                <div className="space-y-3 pt-4 border-t">
                  <p className="text-sm text-[#64748B] mb-2">Where to add?</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleAdd('top')}
                      disabled={loading}
                      variant="outline"
                      className="flex-1 rounded-full"
                      data-testid="add-to-top-btn"
                    >
                      <ArrowUp className="w-4 h-4 mr-1 text-green-600" />
                      Top (#1)
                    </Button>
                    <Button
                      onClick={() => handleAdd('bottom')}
                      disabled={loading}
                      variant="outline"
                      className="flex-1 rounded-full"
                      data-testid="add-to-bottom-btn"
                    >
                      <ArrowDown className="w-4 h-4 mr-1 text-blue-600" />
                      Bottom
                    </Button>
                  </div>
                  {itemCount > 0 && onAddAndRank && (
                    <Button
                      onClick={handleAddAndRank}
                      disabled={loading}
                      className="w-full bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                      data-testid="add-and-rank-btn"
                    >
                      <ListOrdered className="w-4 h-4 mr-2" />
                      Add & Rank
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddToListDialog;
